import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';

const HEART_BURST_COUNT = 6;

/** Grace period after connect during which a counter increase is treated as Swish's own async
 * initial-state hydration rather than a live add, so returning customers with existing items don't
 * get a spurious heart-burst on page load (see #onMutation). */
const HYDRATION_GRACE_PERIOD_MS = 1500;

/**
 * A custom element wrapping the Swish "Instant Header Link" wishlist icon.
 *
 * Swish's own injected script (added via their app's Code Access, outside this repo) hydrates
 * `.wkh-button`/`.wkh-counter` and is the only thing that adds items to the wishlist, so it's also
 * the only place we can observe an add happening from theme code: we watch `.wkh-counter` for its
 * text going up and burst hearts out of the icon - this component's equivalent of the `fly-to-cart`
 * animation in product-form.js, but a burst of hearts instead of a single flying element.
 *
 * @typedef {object} Refs
 * @property {HTMLTemplateElement} heartTemplate - Template used to clone heart particles.
 *
 * @extends {Component<Refs>}
 */
class WishlistHeaderLink extends Component {
  requiredRefs = ['heartTemplate'];

  /** @type {number} */
  #previousCount = 0;

  /** @type {number} */
  #connectedAt = 0;

  #onMutation = () => {
    const count = this.#currentCount;
    const isLiveChange = Date.now() - this.#connectedAt > HYDRATION_GRACE_PERIOD_MS;

    if (isLiveChange && count > this.#previousCount) this.#burstHearts();

    this.#previousCount = count;
  };

  #counterObserver = new MutationObserver(this.#onMutation);

  connectedCallback() {
    super.connectedCallback();

    this.#connectedAt = Date.now();
    this.#previousCount = this.#currentCount;
    this.#counterObserver.observe(this, { characterData: true, childList: true, subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#counterObserver.disconnect();
  }

  get #currentCount() {
    const counter = this.querySelector('.wkh-counter');
    return parseInt(counter?.textContent ?? '0', 10) || 0;
  }

  /**
   * Spawns a handful of heart particles that fly out and fade from the wishlist icon.
   *
   * Appended to `this` rather than into `.wkh-button` because Swish re-renders that element's
   * contents on its own, which would otherwise wipe the particles mid-animation.
   */
  #burstHearts() {
    const heartNode = this.refs.heartTemplate.content.firstElementChild;

    if (!heartNode) return;

    for (let i = 0; i < HEART_BURST_COUNT; i++) {
      const heart = /** @type {HTMLElement} */ (heartNode.cloneNode(true));

      // Spread mostly upward (-90deg) with some left/right drift, avoiding downward trajectories.
      const angle = -90 + (Math.random() - 0.5) * 140;
      const distance = 40 + Math.random() * 40;
      const radians = (angle * Math.PI) / 180;

      heart.style.setProperty('--wishlist-heart-tx', `${Math.cos(radians) * distance}px`);
      heart.style.setProperty('--wishlist-heart-ty', `${Math.sin(radians) * distance}px`);
      heart.style.setProperty('--wishlist-heart-rotate', `${(Math.random() - 0.5) * 90}deg`);
      heart.style.setProperty('--wishlist-heart-scale', `${0.6 + Math.random() * 0.6}`);
      heart.style.animationDelay = `${Math.random() * 120}ms`;

      this.appendChild(heart);
      onAnimationEnd(heart, () => heart.remove());
    }
  }
}

if (!customElements.get('wishlist-header-link')) {
  customElements.define('wishlist-header-link', WishlistHeaderLink);
}
